import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useStore } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import Sidebar from "@/components/sidebar";
import ModuleTabs from "@/components/module-tabs";
import { ActivityDropdown } from "@/components/ui/activity-dropdown";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  UserX,
  Activity,
  X,
  Menu,
  ChevronLeft,
  BarChart3,
  Shield,
  Bell,
  BellOff,
  MapPin,
  Unplug,
  TrendingUp,
  Gauge,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Send,
  Pause,
  ArrowDownRight,
  ArrowUpRight,
  Search,
  Wrench,
  Filter,
  ChevronDown,
  Check,
  Sparkles,
  Loader2,
  Trophy,
  TriangleAlert,
  Video,
  Package,
  ShoppingCart,
  Handshake,
  Phone,
  Store,
  RefreshCcw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import type { QuickAction } from "@/components/ui/activity-dropdown";
import { MonitoringGuidelinesPanel } from "@/components/monitoring-guidelines-panel";
import type { TFunction } from "i18next";

function resolveAlertParams(
  params: Record<string, string | number> | null | undefined,
  t: TFunction,
  locale: string
): Record<string, string | number> {
  if (!params) return {};
  const currency = String(params.currency || "PLN");
  const fmtCurrency = (v: number) =>
    new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(
      v
    );
  const fmtRoas = (v: number) => `${v.toFixed(2)}x`;

  const out: Record<string, string | number> = { ...params };
  if (params.labelKey) out.label = t(String(params.labelKey));
  if (params.conversionNounKey) out.conversionNoun = t(String(params.conversionNounKey));
  if (params.periodTypeKey) out.periodTypeLabel = t(String(params.periodTypeKey));

  if (params.formatKey !== "preformatted") {
    const fmt = params.formatKey === "roas" ? fmtRoas : fmtCurrency;
    if (params.actual !== undefined) out.actualFormatted = fmt(params.actual as number);
    if (params.target !== undefined) out.targetFormatted = fmt(params.target as number);
    if (params.spend !== undefined) out.spendFormatted = fmtCurrency(params.spend as number);
  }

  return out;
}

function translateAlertText(
  code: string | null | undefined,
  params: Record<string, string | number> | null | undefined,
  fallback: string,
  t: TFunction,
  locale: string
): string {
  if (!code) return fallback;
  const resolved = resolveAlertParams(params, t, locale);
  return t(code, { ...resolved, defaultValue: fallback });
}

interface MonitoringAlert {
  id: string;
  alertType: string;
  severity: string;
  accountId: string | null;
  accountName: string | null;
  metricName: string | null;
  actualValue: string | null;
  targetValue: string | null;
  message: string;
  recommendation: string | null;
  dedupeKey: string | null;
  isRead: boolean;
  createdAt: string;
  messageCode?: string | null;
  messageParams?: Record<string, string | number> | null;
  recommendationCode?: string | null;
  recommendationParams?: Record<string, string | number> | null;
}

interface AlertFeedback {
  id: string;
  alertId: string;
  rating: number;
  comment: string | null;
  isGlobal: boolean;
  createdAt: string;
}

const ALERT_ICON_MAP: Record<string, typeof AlertTriangle> = {
  cpl_exceeded: DollarSign,
  cpc_exceeded: DollarSign,
  roas_below: TrendingDown,
  no_leads: UserX,
  spend_anomaly: Activity,
  placement_waste: MapPin,
  zero_conversions: Unplug,
  budget_increase: TrendingUp,
  high_cpm: Gauge,
  early_winner_signal: Trophy,
  early_loser_signal: TriangleAlert,
  webinar_low_show_rate: Video,
  webinar_low_offer_rate: Video,
  shopify_low_stock: Package,
  shopify_out_of_stock: Package,
  shopify_revenue_drop: ShoppingCart,
  crm_low_close_rate: Handshake,
  crm_low_show_rate: UserX,
  crm_no_new_leads: UserX,
  baselinker_low_stock: Package,
  baselinker_revenue_drop: ShoppingCart,
  ringostat_low_answer_rate: Phone,
  ringostat_low_call_volume: Phone,
  allegro_sales_drop: Store,
  allegro_low_roas: Store,
  hubspot_low_close_rate: Handshake,
  hubspot_pipeline_stall: RefreshCcw,
};

const SEVERITY_STYLES: Record<string, { bg: string; border: string; badge: string }> = {
  critical: {
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    badge: "bg-red-500/20 text-red-400 border-red-500/30",
  },
  warning: {
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/30",
    badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  },
  info: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
};

// SEVERITY_LABELS removed — use t('monitoring.severity.{severity}') at call sites

function formatTimeAgo(
  dateStr: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return t("monitoring.timeAgo.justNow");
  if (diffMin < 60) return t("monitoring.timeAgo.minutesAgo", { count: diffMin });

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t("monitoring.timeAgo.hoursAgo", { count: diffHours });

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return t("monitoring.timeAgo.yesterday");
  return t("monitoring.timeAgo.daysAgo", { count: diffDays });
}

const METRIC_SUFFIX: Record<string, string> = {
  cost_per_lead: " zł",
  cost_per_call: " zł",
  cpm: " zł",
  roas: "x",
  placement_spend_share: "%",
};

function formatMetricValue(raw: string, metricName: string | null): string {
  const val = parseFloat(raw);
  if (isNaN(val)) return raw;
  const suffix = METRIC_SUFFIX[metricName ?? ""] ?? "";
  return `${val.toFixed(2)}${suffix}`;
}

function hasCampaignInDedupeKey(dedupeKey: string | null): boolean {
  if (!dedupeKey) return false;
  const parts = dedupeKey.split(":");
  const lastPart = parts[parts.length - 1];
  return !!lastPart && /^\d+$/.test(lastPart);
}

function computeStats(alerts: MonitoringAlert[]) {
  const total = alerts.length;
  const unread = alerts.filter((a) => !a.isRead).length;
  const critical = alerts.filter((a) => a.severity === "critical").length;
  const warning = alerts.filter((a) => a.severity === "warning").length;
  return { total, unread, critical, warning };
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: typeof Shield;
  color: string;
}) {
  return (
    <div className={`rounded-xl border ${color} p-6 flex items-center gap-4`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-heading-2 font-extrabold text-white">{value}</p>
        <p className="text-caption font-light text-white/50">{label}</p>
      </div>
    </div>
  );
}

function AlertFeedbackPanel({
  alertId,
  existingFeedback,
  onSubmit,
  isPending,
}: {
  alertId: string;
  existingFeedback?: AlertFeedback;
  onSubmit: (alertId: string, rating: number, comment?: string) => void;
  isPending: boolean;
}) {
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [pendingRating, setPendingRating] = useState<number | null>(null);
  const { t } = useTranslation();

  const hasPositive = existingFeedback?.rating === 1;
  const hasNegative = existingFeedback?.rating === 0;

  function handleThumbClick(rating: number) {
    if (rating === 0) {
      setPendingRating(0);
      setShowComment(true);
      return;
    }
    // Thumbs up — can submit immediately or open comment
    if (showComment && pendingRating === 1) {
      onSubmit(alertId, 1, comment || undefined);
      setShowComment(false);
      setComment("");
      setPendingRating(null);
      return;
    }
    setPendingRating(1);
    setShowComment(true);
  }

  function handleSubmitComment() {
    if (pendingRating === null) return;
    onSubmit(alertId, pendingRating, comment || undefined);
    setShowComment(false);
    setComment("");
    setPendingRating(null);
  }

  return (
    <div className="mt-1">
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => handleThumbClick(1)}
          disabled={isPending}
          className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors text-[11px] ${
            hasPositive
              ? "bg-emerald-500/20 text-emerald-400"
              : "text-white/25 hover:text-emerald-400 hover:bg-white/5"
          }`}
          title={
            hasPositive
              ? t("monitoring.feedback.ratedPositiveTitle")
              : t("monitoring.feedback.goodAlertTitle")
          }
        >
          <ThumbsUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => handleThumbClick(0)}
          disabled={isPending}
          className={`flex items-center gap-1 px-2 py-1 rounded-md transition-colors text-[11px] ${
            hasNegative
              ? "bg-red-500/20 text-red-400"
              : "text-white/25 hover:text-red-400 hover:bg-white/5"
          }`}
          title={
            hasNegative
              ? t("monitoring.feedback.ratedNegativeTitle")
              : t("monitoring.feedback.badAlertTitle")
          }
        >
          <ThumbsDown className="w-3.5 h-3.5" />
        </button>
        {existingFeedback?.comment && !showComment && (
          <span className="text-white/20 text-[10px] italic truncate max-w-[200px]">
            „{existingFeedback.comment}"
          </span>
        )}
      </div>

      <AnimatePresence>
        {showComment && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  pendingRating === 1
                    ? t("monitoring.feedback.goodPlaceholder")
                    : t("monitoring.feedback.badPlaceholder")
                }
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-white/25 focus:outline-none focus:border-white/20"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSubmitComment();
                }}
                autoFocus
              />
              <button
                onClick={handleSubmitComment}
                disabled={isPending}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors text-xs"
              >
                <Send className="w-3 h-3" />
                {t("monitoring.feedback.sendBtn")}
              </button>
              <button
                onClick={() => {
                  setShowComment(false);
                  setPendingRating(null);
                  setComment("");
                }}
                className="px-2 py-1.5 rounded-lg text-white/25 hover:text-white/50 hover:bg-white/5 transition-colors text-xs"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AlertAnalysisPanel({
  alertId,
  isOpen,
  onToggle,
}: {
  alertId: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleAnalyze = useCallback(async () => {
    if (analysis) {
      onToggle();
      return;
    }
    setIsLoading(true);
    setError(null);
    onToggle();
    try {
      const res = await apiRequest("POST", `/api/monitoring/alerts/${alertId}/analyze`);
      const data = res as { analysis: string };
      setAnalysis(data.analysis);
    } catch {
      setError(t("monitoring.analysis.failed"));
    } finally {
      setIsLoading(false);
    }
  }, [alertId, analysis, onToggle]);

  return (
    <div className="mt-2">
      <button
        onClick={handleAnalyze}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-500/20 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 transition-colors text-[11px] font-medium disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Sparkles className="w-3.5 h-3.5" />
        )}
        {isLoading
          ? t("monitoring.analysis.analyzing")
          : analysis
            ? isOpen
              ? t("monitoring.analysis.hide")
              : t("monitoring.analysis.show")
            : t("monitoring.analysis.analyze")}
      </button>

      <AnimatePresence>
        {isOpen && (analysis || isLoading || error) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 p-3 rounded-xl bg-purple-500/5 border border-purple-500/15">
              {isLoading && (
                <div className="flex items-center gap-2 text-purple-300/60 text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  {t("monitoring.analysis.generating")}
                </div>
              )}
              {error && <p className="text-amber-400/70 text-xs">{error}</p>}
              {analysis && !isLoading && (
                <div className="prose prose-invert prose-xs max-w-none text-[12px] leading-relaxed text-white/70 [&_strong]:text-white/90 [&_h1]:text-sm [&_h2]:text-[13px] [&_h3]:text-[12px] [&_ul]:ml-0 [&_ol]:ml-0 [&_li]:my-0.5">
                  <ReactMarkdown>{analysis}</ReactMarkdown>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MonitoringContent() {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const { data: alerts = [], isLoading } = useQuery<MonitoringAlert[]>({
    queryKey: ["/api/monitoring/alerts"],
    refetchInterval: 4 * 60 * 60 * 1000,
  });

  interface ClientAccountMapping {
    clients: string[];
    accountIdToClient: Record<string, string>;
    clientOptions?: Array<{ id: string; name: string }>;
  }

  const { data: clientMapping } = useQuery<ClientAccountMapping>({
    queryKey: ["/api/monitoring/client-accounts"],
  });

  const agencyClients = clientMapping?.clients ?? [];
  const accountIdToClient = clientMapping?.accountIdToClient ?? {};

  const { toast } = useToast();

  const { data: feedbacks = [] } = useQuery<AlertFeedback[]>({
    queryKey: ["/api/monitoring/alerts/feedback"],
  });

  const feedbackByAlertId = new Map(feedbacks.map((f) => [f.alertId, f]));

  const [openAnalysisId, setOpenAnalysisId] = useState<string | null>(null);

  const toggleAnalysis = useCallback((alertId: string) => {
    setOpenAnalysisId((prev) => (prev === alertId ? null : alertId));
  }, []);

  const feedbackMutation = useMutation({
    mutationFn: ({
      alertId,
      rating,
      comment,
    }: {
      alertId: string;
      rating: number;
      comment?: string;
    }) => apiRequest("POST", `/api/monitoring/alerts/${alertId}/feedback`, { rating, comment }),
    onSuccess: (_data: { ok: boolean; isGlobal: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/feedback"] });
      toast({ title: t("monitoring.feedback.saved") });
    },
    onError: () => {
      toast({ title: t("monitoring.feedback.saveAgain"), variant: "destructive" });
    },
  });

  function handleFeedbackSubmit(alertId: string, rating: number, comment?: string) {
    feedbackMutation.mutate({ alertId, rating, comment });
  }

  const executeActionMutation = useMutation({
    mutationFn: ({ alertId, actionType }: { alertId: string; actionType: string }) =>
      apiRequest("POST", `/api/monitoring/alerts/${alertId}/execute-action`, { actionType }),
    onSuccess: (data: { success: boolean; actionDescription?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
      toast({ title: data.actionDescription || t("monitoring.actions.actionDone") });
    },
    onError: (error: { message?: string }) => {
      toast({
        title: error.message || t("monitoring.actions.actionError"),
        variant: "destructive",
      });
    },
  });

  function buildQuickActions(alert: MonitoringAlert): QuickAction[] {
    const hasCampaignId = hasCampaignInDedupeKey(alert.dedupeKey);
    const isPending = executeActionMutation.isPending;

    const ACTIONS_BY_TYPE: Record<string, QuickAction[]> = {
      cpl_exceeded: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
      ],
      cpc_exceeded: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
      ],
      roas_below: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
      ],
      no_leads: [
        {
          id: "pause_all",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_all" }),
          isPending,
        },
        {
          id: "check",
          label: t("monitoring.actions.checkTracking"),
          icon: <Search className="w-3 h-3" />,
          variant: "warning",
          onExecute: () => window.open("https://business.facebook.com/events_manager", "_blank"),
          isPending: false,
        },
      ],
      zero_conversions: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
        {
          id: "check",
          label: t("monitoring.actions.checkEventsManager"),
          icon: <Search className="w-3 h-3" />,
          variant: "warning",
          onExecute: () => window.open("https://business.facebook.com/events_manager", "_blank"),
          isPending: false,
        },
      ],
      placement_waste: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
        {
          id: "settings",
          label: t("monitoring.actions.editPlacements"),
          icon: <Wrench className="w-3 h-3" />,
          variant: "warning",
          onExecute: () => window.open("https://adsmanager.facebook.com", "_blank"),
          isPending: false,
        },
      ],
      budget_increase: [
        {
          id: "increase",
          label: t("monitoring.actions.increaseBudget"),
          icon: <ArrowUpRight className="w-3 h-3" />,
          variant: "success",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "increase_budget" }),
          isPending,
        },
      ],
      high_cpm: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
      ],
      spend_anomaly: [
        {
          id: "pause",
          label: t("monitoring.actions.pauseCampaign"),
          icon: <Pause className="w-3 h-3" />,
          variant: "danger",
          onExecute: () =>
            executeActionMutation.mutate({ alertId: alert.id, actionType: "pause_campaign" }),
          isPending,
        },
      ],
      monthly_kpi_behind: [
        {
          id: "analyze",
          label: t("monitoring.actions.goToAnalyst"),
          icon: <BarChart3 className="w-3 h-3" />,
          variant: "warning",
          onExecute: () => (window.location.href = "/marketing-analyst"),
          isPending: false,
        },
      ],
      quarterly_kpi_behind: [
        {
          id: "analyze",
          label: t("monitoring.actions.goToAnalyst"),
          icon: <BarChart3 className="w-3 h-3" />,
          variant: "warning",
          onExecute: () => (window.location.href = "/marketing-analyst"),
          isPending: false,
        },
      ],
    };

    const actions = ACTIONS_BY_TYPE[alert.alertType] || [];
    // Filter out campaign-specific actions if no campaignId available
    if (!hasCampaignId) {
      return actions.filter((a) => !["pause", "increase"].includes(a.id));
    }
    return actions;
  }

  const { data: settings } = useQuery<{
    monitoringEnabled?: boolean;
    lastMonitoringCheckAt?: string | null;
  }>({
    queryKey: ["/api/analytics-settings"],
  });

  const markReadMutation = useMutation({
    mutationFn: (alertId: string) => apiRequest("PATCH", `/api/monitoring/alerts/${alertId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/monitoring/alerts/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (alertId: string) => apiRequest("DELETE", `/api/monitoring/alerts/${alertId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const toggleMonitoringMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiRequest("PUT", "/api/analytics-settings", { monitoringEnabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics-settings"] });
    },
  });

  const manualCheckMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/monitoring/check"),
    onSuccess: (data: { alerts: number; message: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/analytics-settings"] });
      toast({ title: data.message });
    },
    onError: (error: any) => {
      const msg = error?.message || t("monitoring.actions.checkError");
      toast({ title: msg, variant: "destructive" });
    },
  });

  const canManualCheck = (() => {
    const lastCheck = settings?.lastMonitoringCheckAt;
    if (!lastCheck) return true;
    const elapsed = Date.now() - new Date(lastCheck).getTime();
    return elapsed >= 4 * 60 * 60 * 1000;
  })();

  const nextCheckLabel = (() => {
    const lastCheck = settings?.lastMonitoringCheckAt;
    if (!lastCheck) return null;
    const elapsed = Date.now() - new Date(lastCheck).getTime();
    const cooldown = 4 * 60 * 60 * 1000;
    if (elapsed >= cooldown) return null;
    const remainingMin = Math.ceil((cooldown - elapsed) / 60000);
    const h = Math.floor(remainingMin / 60);
    const m = remainingMin % 60;
    return h > 0 ? `za ${h}h ${m}min` : `za ${m}min`;
  })();

  const monitoringEnabled = settings?.monitoringEnabled ?? false;

  const { selectedClientName: globalSelectedClientName } = useAgencyClients();

  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  // Sync monitoring filter with global client selection
  useEffect(() => {
    setSelectedClient(globalSelectedClientName ?? null);
  }, [globalSelectedClientName]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false);
      }
    }
    if (clientDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [clientDropdownOpen]);

  const uniqueClients = agencyClients;

  const filteredAlerts = selectedClient
    ? alerts.filter((a) => a.accountId && accountIdToClient[a.accountId] === selectedClient)
    : alerts;

  const stats = computeStats(filteredAlerts);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-3 h-3 rounded-full ${monitoringEnabled ? "bg-emerald-400 animate-pulse" : "bg-white/20"}`}
            />
            <h1 className="text-xl font-bold text-white tracking-wide">{t("monitoring.title")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={!canManualCheck || manualCheckMutation.isPending}
              onClick={() => manualCheckMutation.mutate()}
              className="h-8 text-xs border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white disabled:opacity-30"
              title={
                nextCheckLabel
                  ? t("monitoring.checkTitle", { when: nextCheckLabel })
                  : t("monitoring.checkBtn")
              }
            >
              <RefreshCw
                className={`w-3.5 h-3.5 mr-1.5 ${manualCheckMutation.isPending ? "animate-spin" : ""}`}
              />
              {manualCheckMutation.isPending
                ? t("monitoring.checkingBtn")
                : nextCheckLabel
                  ? t("monitoring.unlockBtn", { when: nextCheckLabel })
                  : t("monitoring.checkBtn")}
            </Button>
            <span className="text-sm text-white/50">
              {monitoringEnabled ? t("monitoring.enabled") : t("monitoring.disabled")}
            </span>
            <Switch
              checked={monitoringEnabled}
              onCheckedChange={(checked) => toggleMonitoringMutation.mutate(checked)}
            />
          </div>
        </div>

        {/* Stats */}
        {/* Stats — data-dense KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-dense">
          <StatCard
            label={t("monitoring.stats.allAlerts")}
            value={stats.total}
            icon={Bell}
            color="bg-white/5 border-white/10 text-white/70"
          />
          <StatCard
            label={t("monitoring.stats.unread")}
            value={stats.unread}
            icon={BellOff}
            color="bg-amber-500/10 border-amber-500/20 text-amber-400"
          />
          <StatCard
            label={t("monitoring.stats.critical")}
            value={stats.critical}
            icon={AlertTriangle}
            color="bg-red-500/10 border-red-500/20 text-red-400"
          />
          <StatCard
            label={t("monitoring.stats.warnings")}
            value={stats.warning}
            icon={Shield}
            color="bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
          />
        </div>

        {/* Guidelines panel */}
        <MonitoringGuidelinesPanel />

        {/* Client filter */}
        {uniqueClients.length > 1 && (
          <div className="relative" ref={clientDropdownRef}>
            <button
              onClick={() => setClientDropdownOpen((prev) => !prev)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm transition-colors ${
                selectedClient
                  ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                  : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80"
              }`}
            >
              <Filter className="w-4 h-4" />
              {selectedClient ?? t("monitoring.clientFilter.allClients")}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${clientDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            <AnimatePresence>
              {clientDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute left-0 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-white/10 bg-popover shadow-2xl overflow-hidden"
                >
                  <button
                    onClick={() => {
                      setSelectedClient(null);
                      setClientDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                      !selectedClient
                        ? "bg-white/10 text-white"
                        : "text-white/60 hover:bg-white/5 hover:text-white/80"
                    }`}
                  >
                    {!selectedClient && <Check className="w-3.5 h-3.5 text-amber-400" />}
                    <span className={!selectedClient ? "" : "pl-[22px]"}>
                      {t("monitoring.clientFilter.allClients")}
                    </span>
                  </button>
                  {uniqueClients.map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        setSelectedClient(name);
                        setClientDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                        selectedClient === name
                          ? "bg-white/10 text-white"
                          : "text-white/60 hover:bg-white/5 hover:text-white/80"
                      }`}
                    >
                      {selectedClient === name && <Check className="w-3.5 h-3.5 text-amber-400" />}
                      <span className={selectedClient === name ? "" : "pl-[22px]"}>{name}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Activity Dropdown — all alerts with actions */}
        <ActivityDropdown
          items={filteredAlerts.map((alert) => {
            const Icon = ALERT_ICON_MAP[alert.alertType] ?? AlertTriangle;
            const styles = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.info;
            const iconBgMap: Record<string, string> = {
              critical: "bg-red-500/20 text-red-400",
              warning: "bg-yellow-500/20 text-yellow-400",
              info: "bg-blue-500/20 text-blue-400",
            };
            return {
              id: alert.id,
              icon: <Icon className="h-4 w-4" />,
              iconBg: iconBgMap[alert.severity] ?? iconBgMap.info,
              title: alert.accountName ?? "Alert",
              description: translateAlertText(
                alert.messageCode,
                alert.messageParams,
                alert.message,
                t,
                i18n.language
              ),
              recommendation:
                translateAlertText(
                  alert.recommendationCode,
                  alert.recommendationParams,
                  alert.recommendation ?? "",
                  t,
                  i18n.language
                ) || undefined,
              time: formatTimeAgo(alert.createdAt, t),
              isRead: alert.isRead,
              severityBadge: {
                label: t(`monitoring.severity.${alert.severity}`, { defaultValue: alert.severity }),
                className: styles.badge,
              },
              metricDisplay:
                alert.metricName && alert.actualValue && parseFloat(alert.targetValue ?? "0") > 0
                  ? {
                      actual: formatMetricValue(alert.actualValue, alert.metricName),
                      target: formatMetricValue(alert.targetValue ?? "0", alert.metricName),
                    }
                  : undefined,
              quickActions: buildQuickActions(alert),
              analysisPanel: (
                <AlertAnalysisPanel
                  alertId={alert.id}
                  isOpen={openAnalysisId === alert.id}
                  onToggle={() => toggleAnalysis(alert.id)}
                />
              ),
              feedbackPanel: (
                <AlertFeedbackPanel
                  alertId={alert.id}
                  existingFeedback={feedbackByAlertId.get(alert.id)}
                  onSubmit={handleFeedbackSubmit}
                  isPending={feedbackMutation.isPending}
                />
              ),
              onMarkRead: alert.isRead ? undefined : () => markReadMutation.mutate(alert.id),
              onDismiss: () => dismissMutation.mutate(alert.id),
            };
          })}
          label={
            stats.unread > 0
              ? t("monitoring.alerts.unreadLabel", { count: stats.unread })
              : t("monitoring.alerts.allLabel", { count: filteredAlerts.length })
          }
          sublabel={t("monitoring.alerts.sublabel")}
          onMarkAllRead={stats.unread > 0 ? () => markAllReadMutation.mutate() : undefined}
          hasUnread={stats.unread > 0}
        />

        {!isLoading && !monitoringEnabled && alerts.length === 0 && (
          <div className="text-center py-20 space-y-3">
            <Activity className="w-14 h-14 text-white/10 mx-auto" />
            <p className="text-white/40 text-base font-medium">
              {t("monitoring.empty.disabledTitle")}
            </p>
            <p className="text-white/25 text-sm">{t("monitoring.empty.disabledDesc")}</p>
          </div>
        )}

        {!isLoading && monitoringEnabled && alerts.length === 0 && (
          <div className="text-center py-20 space-y-3">
            <Activity className="w-14 h-14 text-emerald-500/30 mx-auto" />
            <p className="text-white/50 text-base font-medium">
              {t("monitoring.empty.allOkTitle")}
            </p>
            <p className="text-white/30 text-sm">{t("monitoring.empty.allOkDesc")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MonitoringPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [location, setLocation] = useLocation();

  const { setModules } = useStore();

  const { data: modules } = useQuery({
    queryKey: ["/api/modules"],
    queryFn: () => apiRequest("GET", "/api/modules"),
  });

  useEffect(() => {
    if (modules && Array.isArray(modules)) {
      setModules(modules);
    }
  }, [modules, setModules]);

  return (
    <div className="flex h-full bg-[#030407] relative overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Toggle Button - desktop */}
      <motion.button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="hidden md:flex fixed top-4 left-4 z-50 p-3 bg-[#0a0c10]/90 backdrop-blur-md text-white rounded-xl hover:bg-[#12151c]/90 transition-all duration-200 shadow-lg border border-white/[0.08] btn-modern"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronLeft size={20} />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={20} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30, opacity: { duration: 0.2 } }}
            className="fixed md:relative inset-y-0 left-0 z-40 w-72 lg:w-80 h-full bg-[#0a0c10] flex flex-col overscroll-behavior-none glass-morphism border-r border-white/[0.08]"
          >
            <Sidebar />
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 relative overflow-hidden h-full">
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-10 flex items-center justify-between p-4 bg-[#0a0c10] border-b border-white/[0.08]">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center space-x-2">
            <img
              src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
              alt="Viral Partners Logo"
              className="w-8 h-8 object-contain"
            />
            <div>
              <h1 className="text-white font-semibold text-sm">VIRAL PARTNERS</h1>
              <p className="text-white/60 text-xs">MarketingOS</p>
            </div>
          </div>
          <button
            onClick={() => setLocation("/dashboard")}
            className="p-2 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
          >
            <BarChart3 size={22} />
          </button>
        </div>

        {/* Module Tabs Header */}
        <header className="hidden md:block sticky top-0 z-40 bg-[#030407]/95 backdrop-blur-sm border-b border-white/[0.08]">
          <div className="h-20 w-full overflow-hidden">
            <ModuleTabs />
          </div>
        </header>

        {/* Monitoring Content */}
        <MonitoringContent />
      </main>
    </div>
  );
}
